import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { RESONANCE_URL } from '../../../config/links';

const T = {
  bg: '#0B1012',
  textPrimary: '#F8FAFC',
  textSecondary: 'rgba(248, 250, 252, 0.72)',
  textMuted: 'rgba(248, 250, 252, 0.5)',
  border: 'rgba(248, 250, 252, 0.08)',
  orange: '#F97316',
  orangeHover: '#EA580C',
};

const fInter = "'Inter', system-ui, -apple-system, sans-serif";
const fMono = "'JetBrains Mono', ui-monospace, monospace";

const Section = styled(Box)({
  position: 'relative',
  background: T.bg,
  color: T.textPrimary,
  padding: '140px 0',
  fontFamily: fInter,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-30%',
    right: '-15%',
    width: '70%',
    height: '160%',
    background: `radial-gradient(circle, ${T.orange}1a 0%, transparent 60%)`,
    filter: 'blur(80px)',
    pointerEvents: 'none',
  },
});

const Overline = styled(Box)({
  fontFamily: fMono,
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.25em',
  color: T.orange,
  marginBottom: 24,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 14,
  '&::before': {
    content: '""',
    width: 28,
    height: 1,
    background: T.orange,
    opacity: 0.6,
  },
});

const Headline = styled(Typography)({
  fontSize: 'clamp(2.25rem, 4vw, 3.5rem)',
  fontWeight: 700,
  lineHeight: 1.1,
  letterSpacing: '-0.02em',
  color: T.textPrimary,
  marginBottom: 28,
  maxWidth: '18ch',
  fontFamily: fInter,
});

const Body = styled(Typography)({
  fontSize: '1.05rem',
  lineHeight: 1.65,
  color: T.textSecondary,
  marginBottom: 48,
  maxWidth: '54ch',
  fontFamily: fInter,
});

const FeatureList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  marginBottom: 48,
});

const FeatureItem = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '18px 1fr',
  columnGap: 16,
  alignItems: 'start',
});

const FeatureMark = styled('span')({
  color: T.orange,
  fontSize: 16,
  lineHeight: 1.4,
  fontWeight: 700,
});

const FeatureLabel = styled(Typography)({
  color: T.textPrimary,
  fontSize: '1rem',
  fontWeight: 700,
  letterSpacing: '-0.005em',
  marginBottom: 4,
  fontFamily: fInter,
});

const FeatureDesc = styled(Typography)({
  color: T.textSecondary,
  fontSize: '0.95rem',
  lineHeight: 1.55,
  fontFamily: fInter,
});

const CtaButton = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  fontSize: '1rem',
  fontWeight: 700,
  color: '#FFF',
  textDecoration: 'none',
  padding: '16px 28px',
  background: T.orange,
  borderRadius: 6,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: fInter,
  boxShadow: `0 10px 28px ${T.orange}55`,
  '& .arrow': { transition: 'transform 0.2s ease' },
  '&:hover': {
    background: T.orangeHover,
    transform: 'translateY(-2px)',
    boxShadow: `0 14px 36px ${T.orange}88`,
    '& .arrow': { transform: 'translateX(4px)' },
  },
});

/* ----- Right column: browser-frame screenshot placeholder ----- */
const FrameWrap = styled(Box)({
  width: '100%',
  maxWidth: 620,
  marginLeft: 'auto',
  position: 'relative',
});

const BrowserFrame = styled(Box)({
  width: '100%',
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  background: 'rgba(255,255,255,0.02)',
  boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px ${T.orange}22 inset`,
  overflow: 'hidden',
});

const BrowserBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 14px',
  borderBottom: `1px solid ${T.border}`,
  background: 'rgba(255,255,255,0.015)',
  fontFamily: fMono,
  fontSize: 11,
  color: T.textMuted,
});

const BrowserDot = styled('span')(({ color }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: color,
  opacity: 0.55,
}));

const BrowserUrl = styled('span')({
  marginLeft: 12,
  padding: '4px 12px',
  background: 'rgba(255,255,255,0.04)',
  border: `1px solid ${T.border}`,
  borderRadius: 4,
  letterSpacing: '0.02em',
});

const Screenshot = styled('img')({
  display: 'block',
  width: '100%',
  height: 'auto',
});

/* ----- Stat strip under the frame ----- */
const StatRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 1,
  background: T.border,
  marginTop: 24,
  maxWidth: 620,
  marginLeft: 'auto',
  borderRadius: 6,
  overflow: 'hidden',
});

const StatCell = styled(Box)({
  background: T.bg,
  padding: '20px 18px',
});

const StatLabel = styled(Typography)({
  fontFamily: fMono,
  fontSize: 10,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  color: T.textMuted,
  marginBottom: 6,
});

const StatValue = styled(Typography)({
  fontSize: '1.4rem',
  fontWeight: 700,
  color: T.textPrimary,
  letterSpacing: '-0.01em',
  fontFamily: fInter,
});

const ResonanceSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const screenshotSrc =
    lang === 'ja'
      ? '/images/sustefylanding1jp.png'
      : '/images/sustefylanding1en.png';

  return (
    <Section id="resonance-section">
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 8, md: 10 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Overline>{t('landing.resonance.overline')}</Overline>
            <Headline>{t('landing.resonance.title')}</Headline>
            <Body>{t('landing.resonance.body')}</Body>

            <FeatureList>
              {['quality', 'fit', 'portfolio'].map((key) => (
                <FeatureItem key={key}>
                  <FeatureMark>✦</FeatureMark>
                  <Box>
                    <FeatureLabel>{t(`landing.resonance.features.${key}.label`)}</FeatureLabel>
                    <FeatureDesc>{t(`landing.resonance.features.${key}.desc`)}</FeatureDesc>
                  </Box>
                </FeatureItem>
              ))}
            </FeatureList>

            <CtaButton href={RESONANCE_URL} target="_blank" rel="noopener noreferrer">
              {t('landing.resonance.cta')}
              <span className="arrow">→</span>
            </CtaButton>
          </Grid>

          <Grid item xs={12} md={6}>
            <FrameWrap>
              <BrowserFrame>
                <BrowserBar>
                  <BrowserDot color="#ff5f56" />
                  <BrowserDot color="#ffbd2e" />
                  <BrowserDot color="#27c93f" />
                  <BrowserUrl>resonance.sustefy.com</BrowserUrl>
                </BrowserBar>
                <Screenshot
                  src={screenshotSrc}
                  alt={t('landing.resonance.title')}
                />

              </BrowserFrame>

              <StatRow>
                <StatCell>
                  <StatLabel>{t('landing.resonance.stat1Label')}</StatLabel>
                  <StatValue>{t('landing.resonance.stat1Value')}</StatValue>
                </StatCell>
                <StatCell>
                  <StatLabel>{t('landing.resonance.stat2Label')}</StatLabel>
                  <StatValue>{t('landing.resonance.stat2Value')}</StatValue>
                </StatCell>
                <StatCell>
                  <StatLabel>{t('landing.resonance.stat3Label')}</StatLabel>
                  <StatValue>{t('landing.resonance.stat3Value')}</StatValue>
                </StatCell>
              </StatRow>
            </FrameWrap>
          </Grid>
        </Grid>
      </Container>
    </Section>
  );
};

export default ResonanceSection;
